import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr, fmt, monthStart, today } from "@/lib/api";
import { Button, Input, Label, Card, Modal, StatusPill } from "@/components/kit";
import { Plus, Link2, FileText, Trash2, ExternalLink, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Signoffs() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ project_id: "", date_from: monthStart(), date_to: today(), title: "", show_amounts: false, include_photos: true, include_notes: true });

  const load = () => api.get("/signoffs").then((r) => setItems(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); api.get("/projects").then((r) => setProjects(r.data)); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.project_id) return toast.error("Välj projekt");
    try {
      await api.post("/signoffs", form);
      toast.success("Attestlänk skapad");
      setOpen(false); load();
    } catch (err) { toast.error(apiErr(err)); }
  };
  const del = async (id) => { if (!window.confirm("Ta bort attest?")) return; await api.delete(`/signoffs/${id}`); load(); };
  const emailLink = async (id) => { try { const { data } = await api.post(`/signoffs/${id}/email`, { base_url: window.location.origin }); toast.success("Mejlat till " + data.to); load(); } catch (e) { toast.error(apiErr(e)); } };
  const markInvoiced = async (id) => { try { await api.put(`/signoffs/${id}/status?status=invoiced`); toast.success("Markerad som fakturerad"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const copyLink = (token) => {
    const url = `${window.location.origin}/sign/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Länk kopierad: " + url);
  };

  return (
    <div className="rise">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-amber-500">Fakturering</p>
          <h1 className="font-display text-3xl font-extrabold text-white">Attest & Faktura</h1>
          <p className="mt-1 text-sm text-slate-400">Skapa en länk som beställaren signerar. Generera sedan fakturaunderlag.</p>
        </div>
        <Button data-testid="create-signoff-button" onClick={() => setOpen(true)}><Plus size={16} /> Ny attest</Button>
      </div>

      <div className="space-y-3" data-testid="signoffs-list">
        {items.map((s) => (
          <Card key={s.id} className="p-5" data-testid={`signoff-${s.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <h3 className="font-display text-lg font-bold text-white">{s.title}</h3>
                  <StatusPill status={s.status} />
                </div>
                <div className="mt-1 text-sm text-slate-400">
                  {s.project_name} · {s.date_from} – {s.date_to}
                  {" · "}<span className={s.show_amounts ? "text-amber-400" : "text-slate-500"}>{s.show_amounts ? "belopp delas" : "endast tid/mängd"}</span>
                  {s.include_photos && <> · <span className="text-sky-400">bilder</span></>}
                  {s.include_notes && <> · <span className="text-sky-400">anteckningar</span></>}
                  {s.contractor_name && <> · signerad av <span className="text-slate-200">{s.contractor_name}</span></>}
                  {s.invoice_number && <> · <span className="text-sky-400">Faktura {s.invoice_number}</span></>}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="font-display text-lg font-bold text-amber-400">{fmt(s.summary?.subtotal)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">exkl. moms</div>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="outline" className="px-3 py-2 text-xs" data-testid={`email-signoff-${s.id}`} onClick={() => emailLink(s.id)}><Mail size={14} /> Mejla</Button>
                  <Button variant="outline" className="px-3 py-2 text-xs" data-testid={`copy-link-${s.id}`} onClick={() => copyLink(s.token)}><Link2 size={14} /> Länk</Button>
                  <Button variant="outline" className="px-3 py-2 text-xs" data-testid={`open-signoff-${s.id}`} onClick={() => nav(`/signoffs/${s.id}`)}><FileText size={14} /> Öppna</Button>
                  <a href={`${api.defaults.baseURL}/signoffs/${s.id}/pdf`} target="_blank" rel="noreferrer">
                    <Button variant="dark" className="px-3 py-2 text-xs" data-testid={`pdf-${s.id}`}><ExternalLink size={14} /> PDF</Button>
                  </a>
                  {s.status === "signed" && (
                    <Button variant="primary" className="px-3 py-2 text-xs" data-testid={`invoice-signoff-${s.id}`} onClick={() => markInvoiced(s.id)}><CheckCircle2 size={14} /> Fakturerad</Button>
                  )}
                  <Button variant="danger" className="px-2.5 py-2 text-xs" data-testid={`delete-signoff-${s.id}`} onClick={() => del(s.id)}><Trash2 size={14} /></Button>
                </div>
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && <Card className="p-12 text-center text-slate-500">Inga attester ännu.</Card>}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Ny attest / fakturaunderlag">
        <form onSubmit={create} className="space-y-4">
          <div>
            <Label>Projekt</Label>
            <select data-testid="signoff-project-select" className="w-full rounded-lg bg-slate-950/60 border border-slate-700 px-3 py-2.5 text-sm text-slate-100" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} required>
              <option value="">Välj…</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Från</Label><Input data-testid="signoff-from" type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} required /></div>
            <div><Label>Till</Label><Input data-testid="signoff-to" type="date" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} required /></div>
          </div>
          <div><Label>Titel (valfritt)</Label><Input data-testid="signoff-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Genereras automatiskt" /></div>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-700 bg-slate-950/40 px-4 py-3">
            <input type="checkbox" data-testid="signoff-show-amounts" checked={form.show_amounts} onChange={(e) => setForm({ ...form, show_amounts: e.target.checked })} className="mt-0.5 h-4 w-4 accent-amber-500" />
            <span className="text-sm text-slate-200">Visa belopp för beställaren
              <span className="block text-xs text-slate-500">Av = beställaren ser bara tid och materialmängd (inga kronor)</span></span>
          </label>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-700 bg-slate-950/40 px-4 py-3">
            <input type="checkbox" data-testid="signoff-include-photos" checked={form.include_photos} onChange={(e) => setForm({ ...form, include_photos: e.target.checked })} className="mt-0.5 h-4 w-4 accent-amber-500" />
            <span className="text-sm text-slate-200">Skicka med bilder från perioden
              <span className="block text-xs text-slate-500">Beställaren ser alla foton som lagts in i tidrapporterna för perioden, och de bifogas i PDF:en</span></span>
          </label>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-700 bg-slate-950/40 px-4 py-3">
            <input type="checkbox" data-testid="signoff-include-notes" checked={form.include_notes} onChange={(e) => setForm({ ...form, include_notes: e.target.checked })} className="mt-0.5 h-4 w-4 accent-amber-500" />
            <span className="text-sm text-slate-200">Skicka med anteckningar från perioden
              <span className="block text-xs text-slate-500">Beställaren ser anteckningarna som skrivits i tidrapporterna, och de kommer med i PDF:en</span></span>
          </label>
          <Button data-testid="save-signoff-button" type="submit" className="w-full">Skapa attestlänk</Button>
        </form>
      </Modal>
    </div>
  );
}
